/* Product browsing never changes the print image, placement, consent or cart.
   The editor always shows the shopper's own case; the model-specific magnetic
   hero and shared detail photos live in the close-up disclosure. */
(function () {
  'use strict';
  const views = ['inside', 'edge', 'camera'];
  let current = 'inside';
  let lastModel = '';
  let hasDesign = false;
  let catalogue;
  const failedSources = new Set();
  const $ = selector => document.querySelector(selector);
  const buttons = () => Array.from(document.querySelectorAll('[data-product-view]'));

  function getCatalogue() {
    if (catalogue) return catalogue;
    try { catalogue = JSON.parse($('[data-product-catalogue]')?.textContent || '{}'); }
    catch (_) { catalogue = {}; }
    return catalogue;
  }
  function sourceFor(view) {
    const data = getCatalogue();
    const hero = data.constructionModels?.[lastModel];
    // Unknown models must never borrow a different model's hero.
    if (!hero) return;
    return view === 'inside' ? hero : data.sharedViews?.[view];
  }
  function usable(view) {
    const src = sourceFor(view);
    return !!src && !failedSources.has(src);
  }

  function renderEditor() {
    // The canvas is always the main surface, so it stays interactive and measurable.
    const stage = $('[data-product-stage]');
    if (stage) stage.dataset.productSurface = 'design';
    const canvas = $('#mockup-container');
    if (canvas) {
      canvas.inert = false;
      canvas.removeAttribute('aria-hidden');
    }
    const hint = $('[data-product-empty-hint]');
    if (hint) hint.hidden = hasDesign;
    placeEmptyHint();
  }

  // The empty-case hint belongs in the open print window (below the camera),
  // not the middle of the whole case. Each model's overlay PNG is transparent
  // exactly where the design shows, so measure that window once per image.
  const printWindows = new Map();
  function measurePrintWindow(overlay) {
    const src = overlay.currentSrc || overlay.src;
    if (printWindows.has(src)) return printWindows.get(src);
    let area = null;
    try {
      const w = 100, h = Math.max(1, Math.round(w * overlay.naturalHeight / overlay.naturalWidth));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(overlay, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      const open = (x, y) => data[(y * w + x) * 4 + 3] < 20;
      const longestRun = (length, test) => {
        let best = [0, 0], start = -1;
        for (let i = 0; i <= length; i++) {
          if (i < length && test(i)) { if (start < 0) start = i; }
          else if (start >= 0) { if (i - start > best[1] - best[0]) best = [start, i]; start = -1; }
        }
        return best;
      };
      const rows = longestRun(h, y => open(Math.round(w / 2), y));
      const midY = Math.round((rows[0] + rows[1]) / 2);
      const cols = longestRun(w, x => open(x, midY));
      if (rows[1] - rows[0] > h * 0.2 && cols[1] - cols[0] > w * 0.3) {
        area = { x: (cols[0] + cols[1]) / 2 / w, y: midY / h, width: (cols[1] - cols[0]) / w };
      }
    } catch (_) { /* Cross-origin or unreadable image: keep the CSS centre. */ }
    printWindows.set(src, area);
    return area;
  }
  function placeEmptyHint() {
    const hint = $('[data-product-empty-hint]');
    const stage = $('[data-product-stage]');
    const overlay = $('#mockup-overlay');
    if (!hint || !stage || !overlay || hint.hidden) return;
    const area = overlay.complete && overlay.naturalWidth ? measurePrintWindow(overlay) : null;
    const box = overlay.getBoundingClientRect();
    if (!area || !box.width) {
      hint.style.removeProperty('--cz-hint-x');
      hint.style.removeProperty('--cz-hint-y');
      hint.style.removeProperty('--cz-hint-max');
      return;
    }
    const origin = stage.getBoundingClientRect();
    hint.style.setProperty('--cz-hint-x', `${box.left - origin.left + box.width * area.x}px`);
    hint.style.setProperty('--cz-hint-y', `${box.top - origin.top + box.height * area.y}px`);
    hint.style.setProperty('--cz-hint-max', `${Math.max(80, box.width * area.width - 24)}px`);
  }

  function renderGallery() {
    const gallery = $('[data-product-gallery]');
    const photo = $('[data-product-photo]');
    const image = $('[data-product-image]');
    if (!gallery || !photo || !image) return;
    if (!usable(current)) current = views.find(usable) || 'inside';
    const available = usable(current);
    gallery.hidden = !available;
    if (!available) return;
    const src = sourceFor(current);
    if (image.getAttribute('src') !== src) image.src = src;
    photo.dataset.detail = current;
    buttons().forEach(button => {
      const view = button.dataset.productView;
      button.hidden = !usable(view);
      button.setAttribute('aria-pressed', String(view === current));
    });
    image.alt = (gallery.dataset[current + 'Alt'] || '').replace('[model]', lastModel);
    const caption = $('[data-product-gallery-caption]');
    const label = (gallery.dataset[current + 'Copy'] || '').replace('[model]', lastModel);
    if (caption && caption.textContent !== label) caption.textContent = label;
  }

  function render() {
    renderEditor();
    renderGallery();
    return true;
  }

  function refresh(options) {
    hasDesign = options.hasDesign;
    if (options.model !== lastModel) current = 'inside';
    lastModel = options.model;
    document.querySelectorAll('[data-product-thumbnail]').forEach(thumb => {
      const src = sourceFor(thumb.dataset.productThumbnail);
      if (src && thumb.getAttribute('src') !== src) thumb.src = src;
    });
    return render();
  }

  function showDesign() { render(); }

  function init() {
    // Model selection changes the URL without reloading Shopify's language forms.
    document.querySelectorAll('form[action$="/localization"]').forEach(form => {
      const syncReturnTo = () => {
        const returnTo = form.querySelector('input[name="return_to"]');
        if (returnTo) returnTo.value = window.location.pathname + window.location.search + window.location.hash;
      };
      // Dawn calls form.submit(), which bypasses the submit event. Capture the
      // language link click before its handler performs that native submission.
      form.addEventListener('click', syncReturnTo, true);
      form.addEventListener('submit', syncReturnTo);
    });
    buttons().forEach(button => button.addEventListener('click', () => {
      current = button.dataset.productView;
      render();
    }));
    // The overlay swaps per model and resizes with the viewport.
    $('#mockup-overlay')?.addEventListener('load', placeEmptyHint);
    window.addEventListener('resize', placeEmptyHint, { passive: true });
    const image = $('[data-product-image]');
    function failed() {
      const src = image.getAttribute('src');
      if (!src) return;
      failedSources.add(src);
      render();
    }
    image?.addEventListener('error', failed);
    if (image?.complete && image.getAttribute('src') && !image.naturalWidth) failed();
    window.RoboPixelJourney?.refresh();
  }
  window.RoboPixelProductContext = { refresh, showDesign };
  if (typeof window.RoboPixelCustomizerReady === 'function') window.RoboPixelCustomizerReady(init);
  else document.addEventListener('robopixel:customizer-ready', init, { once: true });
})();
